import type { Tone } from "../../shared/ui";
import type {
  ReopenStatus,
  TimesheetPeriodType,
  TimesheetStatus,
} from "../../shared/types/api";

/**
 * The words and colours timesheets are described with, in one place.
 *
 * <p>The status labels are §75's lifecycle written the way somebody reads it rather than the way
 * it is stored: "Waiting for approval" says who the ball is with, which "SUBMITTED" does not.
 */
export const TIMESHEET_STATUS_LABELS: Record<TimesheetStatus, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Waiting for approval",
  APPROVED: "Approved",
  REJECTED: "Sent back",
  LOCKED: "Locked",
};

export const TIMESHEET_STATUS_TONE: Record<TimesheetStatus, Tone> = {
  DRAFT: "gray",
  SUBMITTED: "amber",
  APPROVED: "green",
  REJECTED: "red",
  /* Not a failure — the period is simply finished with, because payroll or invoicing has
     taken it. Grey rather than red keeps it from reading as a problem to fix. */
  LOCKED: "blue",
};

export const REOPEN_STATUS_LABELS: Record<ReopenStatus, string> = {
  PENDING: "Waiting for a decision",
  APPROVED: "Reopened",
  REJECTED: "Declined",
};

export const REOPEN_STATUS_TONE: Record<ReopenStatus, Tone> = {
  PENDING: "amber",
  APPROVED: "green",
  REJECTED: "red",
};

/**
 * Minutes as people say them: 150 → "2h 30m".
 *
 * <p>Never "2.5h". §69 stores minutes precisely so that totals stay exact, and a decimal display
 * quietly reintroduces the rounding the storage decision was made to avoid — three entries of
 * 1.17h summing to 3.5h is the kind of arithmetic that makes somebody distrust the whole screen.
 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) {
    return "—";
  }
  const whole = Math.abs(Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  const sign = minutes < 0 ? "-" : "";
  if (hours === 0) {
    return `${sign}${rest}m`;
  }
  return rest === 0 ? `${sign}${hours}h` : `${sign}${hours}h ${rest}m`;
}

/** Hours from the API, shown in the same vocabulary as everything else. */
export function formatHoursAsTime(hours: number | null | undefined): string {
  return hours == null ? "—" : formatMinutes(Math.round(hours * 60));
}

/**
 * What somebody types, as minutes.
 *
 * <p>Accepts "2h 30m", "2h", "45m", "2:30", "2.5" and "150m". §69 leaves the entry format open
 * on purpose: the storage is exact either way, and refusing "2h 30m" because the field wanted a
 * decimal is friction with nothing behind it.
 *
 * <p>Returns null for anything it cannot read, so the caller can say so rather than guess.
 */
export function parseDuration(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (text === "") {
    return null;
  }

  const clock = /^(\d{1,2}):([0-5]?\d)$/.exec(text);
  if (clock) {
    return Number(clock[1]) * 60 + Number(clock[2]);
  }

  const composite = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(text);
  if (composite && (composite[1] || composite[2])) {
    const hours = composite[1] ? Number(composite[1]) : 0;
    const minutes = composite[2] ? Number(composite[2]) : 0;
    return Math.round(hours * 60 + minutes);
  }

  // A bare number means hours, which is what people mean when they type "8".
  const bare = /^(\d+(?:\.\d+)?)$/.exec(text);
  if (bare) {
    return Math.round(Number(bare[1]) * 60);
  }

  return null;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** An ISO date parsed as a plain calendar date, with no timezone shifting it a day. */
export function parseIsoDate(iso: string): Date {
  const [year = 1970, month = 1, day = 1] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatDayLong(iso: string): string {
  const date = parseIsoDate(iso);
  return `${DAY_NAMES[date.getDay()] ?? ""} ${date.getDate()} ${MONTH_NAMES[date.getMonth()] ?? ""}`;
}

export function formatMonth(iso: string): string {
  const date = parseIsoDate(iso);
  return `${MONTH_NAMES[date.getMonth()] ?? ""} ${date.getFullYear()}`;
}

/**
 * Names a period.
 *
 * <p>"August 2026" only when the period really is the whole of August. Sharing a month is not
 * enough: under §72's weekly cadence five different weeks sit in an approval queue at once, and
 * calling every one of them "August 2026" leaves an approver unable to tell which they are
 * deciding on.
 */
export function formatPeriod(start: string, end: string): string {
  const from = parseIsoDate(start);
  const to = parseIsoDate(end);
  const wholeMonth =
    from.getFullYear() === to.getFullYear()
    && from.getMonth() === to.getMonth()
    && from.getDate() === 1
    // Day 0 of the next month is the last day of this one, whatever its length.
    && to.getDate() === new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();

  return wholeMonth ? formatMonth(start) : `${formatDayLong(start)} → ${formatDayLong(end)}`;
}

/** First and last day of the month a date falls in — the period a timesheet covers by default. */
export function monthBounds(reference: Date): { periodStart: string; periodEnd: string } {
  const year = reference.getFullYear();
  const month = reference.getMonth();
  const iso = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate(),
    ).padStart(2, "0")}`;
  return {
    periodStart: iso(new Date(year, month, 1)),
    periodEnd: iso(new Date(year, month + 1, 0)),
  };
}

export function shiftMonths(iso: string, months: number): Date {
  const date = parseIsoDate(iso);
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/**
 * The Monday-to-Sunday week a date falls in.
 *
 * <p>Monday first because the working week is what this grid is for — a Sunday-first week puts
 * two non-working days at opposite ends and splits the part people care about.
 */
export function weekBounds(reference: Date): { from: string; to: string } {
  const date = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  // getDay() is 0 for Sunday, so Sunday belongs to the week that started six days earlier.
  const offsetToMonday = (date.getDay() + 6) % 7;
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate() - offsetToMonday);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return { from: isoDate(monday), to: isoDate(sunday) };
}

export function shiftDays(iso: string, days: number): Date {
  const date = parseIsoDate(iso);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

/** "17 Aug – 23 Aug", or "29 Sep – 5 Oct" when the week straddles a month. */
export function formatWeekRange(from: string, to: string): string {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  const startMonth = MONTH_SHORT[start.getMonth()] ?? "";
  const endMonth = MONTH_SHORT[end.getMonth()] ?? "";
  return `${start.getDate()} ${startMonth} – ${end.getDate()} ${endMonth}`;
}

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Mon" and "17", the two things a column header needs. */
export function dayColumnLabel(iso: string): { weekday: string; dayOfMonth: number } {
  const date = parseIsoDate(iso);
  return { weekday: DAY_SHORT[date.getDay()] ?? "", dayOfMonth: date.getDate() };
}

export function isToday(iso: string): boolean {
  return iso === isoDate(new Date());
}


/**
 * The period before or after the one starting on a date (§72).
 *
 * <p>There is deliberately no "the period containing this date" here. The server works that out
 * and refuses anything ragged, so the screen navigates from a boundary the server gave it rather
 * than computing one that might be a day off — and a week that is a day off is not a slightly
 * wrong period but one the database will not store beside its neighbour.
 */
export function shiftPeriod(
  period: TimesheetPeriodType,
  from: string,
  direction: -1 | 1,
): Date {
  return period === "WEEKLY"
    ? shiftDays(from, 7 * direction)
    : shiftMonths(from, direction);
}

/** "24 Aug – 30 Aug" for a week, "August 2026" for a month. */
export function formatPeriodLabel(
  period: TimesheetPeriodType,
  from: string,
  to: string,
): string {
  return period === "WEEKLY" ? formatWeekRange(from, to) : formatMonth(from);
}
